import React, { useState, useEffect, useMemo } from 'react';
import { Card, Tag, Spin, Empty, Typography, Divider, Table, Row, Col, Button, message } from 'antd';
import { TrophyOutlined, UserOutlined, CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { TournamentCategory, Participant, Match } from '@/types/api.types';
import { logger } from '@/utils/logger';
import { api } from '@/api';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const { Title, Text } = Typography;

const BracketContainer = styled.div`
  padding: 24px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
`;

const MatchesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const MatchCard = styled(Card)<{ $isCompleted?: boolean; $isActive?: boolean }>`
  border-radius: 4px;
  border: 1px solid ${props => props.$isActive ? '#1890ff' : props.$isCompleted ? '#52c41a' : '#e8e8e8'};
  box-shadow: ${props => props.$isActive ? '0 2px 4px rgba(24, 144, 255, 0.2)' : props.$isCompleted ? '0 2px 4px rgba(82, 196, 26, 0.2)' : '0 1px 2px rgba(0,0,0,0.05)'};
  transition: all 0.2s ease;
  background: #ffffff;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ParticipantRow = styled.div<{ $isWinner?: boolean; $isLoser?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  margin: 0 0 4px 0;
  background: ${props => {
    if (props.$isWinner) return '#f6ffed';
    if (props.$isLoser) return '#fff1f0';
    return '#ffffff';
  }};
  border: ${props => {
    if (props.$isWinner) return '2px solid #52c41a';
    if (props.$isLoser) return '2px solid #ff4d4f';
    return '1px solid #e8e8e8';
  }};
  border-radius: 4px;
  font-size: 14px;
  font-weight: ${props => props.$isWinner ? 'bold' : 'normal'};
  min-height: 50px;
  box-shadow: ${props => props.$isWinner ? '0 2px 4px rgba(82, 196, 26, 0.2)' : '0 1px 2px rgba(0,0,0,0.05)'};
`;

const ParticipantName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const ScoreDisplay = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #1890ff;
  margin: 0 16px;
`;

// Определяем ParticipantStats перед использованием в StandingsTable
interface ParticipantStats {
  participant: Participant;
  wins: number;
  losses: number;
  draws: number;
  points: number;
  matchesPlayed: number;
  place: number;
}

const StandingsTable = styled(Table<ParticipantStats>)`
  .ant-table-thead > tr > th {
    background: #1890ff;
    color: white;
    font-weight: 600;
    border: none;
  }
  
  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #f0f0f0;
  }
  
  .ant-table-tbody > tr:hover > td {
    background: #f5f5f5;
  }
`;

const MedalBadge = styled.span<{ $place: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-weight: bold;
  font-size: 16px;
  margin-right: 12px;
  background: ${props => {
    if (props.$place === 1) return '#ffd700';
    if (props.$place === 2) return '#c0c0c0';
    if (props.$place === 3) return '#cd7f32';
    return '#f0f0f0';
  }};
  color: ${props => props.$place <= 3 ? '#fff' : '#595959'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

interface RoundRobinBracketProps {
  tournamentId: number;
  category: TournamentCategory;
  participants: Participant[];
}

export const RoundRobinBracket: React.FC<RoundRobinBracketProps> = ({
  tournamentId,
  category,
  participants
}) => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const bracketRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadMatches();
  }, [tournamentId, category?.id]);

  const loadMatches = async () => {
    if (!tournamentId || !category?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/tournaments/${tournamentId}/matches`);
      const matchesData = response.data?.data || response.data || [];
      
      const categoryMatches = Array.isArray(matchesData)
        ? matchesData.filter((match: Match) => match.categoryId === category.id)
        : [];
      
      setMatches(categoryMatches);
    } catch (error) {
      logger.error('Error loading matches:', error);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  };

  // Вычисляем статистику участников
  const standings = useMemo<ParticipantStats[]>(() => {
    const statsMap = new Map<number, ParticipantStats>();

    // Инициализируем статистику для всех участников
    participants.forEach(p => {
      const pId = p.id || (p as any).userId || (p as any).participantId;
      if (pId) {
        statsMap.set(pId, {
          participant: p,
          wins: 0,
          losses: 0,
          draws: 0,
          points: 0,
          matchesPlayed: 0,
          place: 0,
        });
      }
    });

    // Подсчитываем результаты матчей
    matches.forEach(match => {
      if (match.status === 'completed' && match.winnerParticipantId) {
        const winnerId = match.winnerParticipantId;
        const loserId = match.participant1Id === winnerId ? match.participant2Id : match.participant1Id;

        if (winnerId && statsMap.has(winnerId)) {
          const winnerStats = statsMap.get(winnerId)!;
          winnerStats.wins++;
          winnerStats.points += 3;
          winnerStats.matchesPlayed++;
        }

        if (loserId && statsMap.has(loserId)) {
          const loserStats = statsMap.get(loserId)!;
          loserStats.losses++;
          loserStats.matchesPlayed++;
        }
      } else if (match.status === 'completed' && !match.winnerParticipantId) {
        // Ничья (если есть такая логика)
        if (match.participant1Id && statsMap.has(match.participant1Id)) {
          const stats1 = statsMap.get(match.participant1Id)!;
          stats1.draws++;
          stats1.points += 1;
          stats1.matchesPlayed++;
        }
        if (match.participant2Id && statsMap.has(match.participant2Id)) {
          const stats2 = statsMap.get(match.participant2Id)!;
          stats2.draws++;
          stats2.points += 1;
          stats2.matchesPlayed++;
        }
      }
    });

    // Сортируем по очкам, затем по победам
    const sorted = Array.from(statsMap.values()).sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      return b.wins - a.wins;
    });

    // Присваиваем места
    sorted.forEach((stats, index) => {
      stats.place = index + 1;
    });

    return sorted;
  }, [matches, participants]);


  // Получаем участника по ID
  const getParticipant = (participantId?: number): Participant | undefined => {
    if (!participantId) return undefined;
    return participants.find(p => {
      const pId = p.id || (p as any).userId || (p as any).participantId;
      return pId === participantId;
    });
  };

  const exportToPDF = async () => {
    if (!bracketRef.current) {
      message.error('Не удалось найти элемент для экспорта');
      return;
    }

    if (matches.length === 0 && standings.length === 0) {
      message.error('Нет данных для экспорта');
      return;
    }

    try {
      setExporting(true);
      message.loading({ content: 'Создание PDF...', key: 'pdf-export' });

      // Ждем, чтобы убедиться, что контент отрендерен
      await new Promise(resolve => setTimeout(resolve, 300));

      const canvas = await html2canvas(bracketRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: true,
        width: bracketRef.current.scrollWidth || bracketRef.current.offsetWidth,
        height: bracketRef.current.scrollHeight || bracketRef.current.offsetHeight,
      });

      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        message.error({ content: 'Не удалось захватить содержимое для экспорта', key: 'pdf-export' });
        logger.error('Canvas is empty:', { width: canvas?.width, height: canvas?.height });
        return;
      }

      const imgWidth = 297; // A4 landscape width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Если высота больше, чем помещается на A4, масштабируем
      const maxHeight = 210; // A4 landscape height in mm
      let finalWidth = imgWidth;
      let finalHeight = imgHeight;
      
      if (imgHeight > maxHeight) {
        finalHeight = maxHeight;
        finalWidth = (canvas.width * maxHeight) / canvas.height;
      }

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      
      // Если высота больше одной страницы, разбиваем на несколько
      let heightLeft = finalHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, finalWidth, finalHeight);
      heightLeft -= 210; // A4 height

      while (heightLeft > 0) {
        position = heightLeft - finalHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, finalWidth, finalHeight);
        heightLeft -= 210;
      }

      const fileName = `${category.name || 'round_robin_bracket'}.pdf`;
      pdf.save(fileName);

      message.success({ content: 'PDF успешно создан!', key: 'pdf-export' });
    } catch (error) {
      logger.error('Error exporting to PDF:', error);
      message.error({ content: 'Ошибка при создании PDF', key: 'pdf-export' });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <BracketContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </BracketContainer>
    );
  }

  if (participants.length !== 3) {
    return (
      <BracketContainer>
        <Empty description={`Круговая система используется только для 3 участников. В категории ${participants.length} участников.`} />
      </BracketContainer>
    );
  }

  return (
    <BracketContainer ref={bracketRef}>
      <HeaderSection>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <TrophyOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            Круговая система - {category.name}
          </Title>
          <Text type="secondary" style={{ fontSize: '14px', marginTop: '8px', display: 'block' }}>
            Каждый участник играет с каждым. Победитель определяется по количеству побед.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={exportToPDF}
          loading={exporting}
          disabled={matches.length === 0 && standings.length === 0}
        >
          Экспорт в PDF
        </Button>
      </HeaderSection>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Title level={4} style={{ marginBottom: 16 }}>
            Матчи
          </Title>
          <MatchesGrid>
            {matches.map((match) => {
              const isCompleted = match.status === 'completed';
              const isActive = match.status === 'in_progress';
              const participant1 = getParticipant(match.participant1Id);
              const participant2 = getParticipant(match.participant2Id);
              const isWinner1 = match.winnerParticipantId === match.participant1Id;
              const isWinner2 = match.winnerParticipantId === match.participant2Id;

              return (
                <MatchCard
                  key={match.id}
                  $isCompleted={isCompleted}
                  $isActive={isActive}
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Матч #{match.matchNumber || match.id}</span>
                      <Tag color={isCompleted ? 'success' : isActive ? 'processing' : 'default'}>
                        {isCompleted ? 'Завершен' : isActive ? 'В процессе' : 'Ожидание'}
                      </Tag>
                    </div>
                  }
                  extra={
                    match.tatamiNumber && (
                      <Tag color="blue">Татами {match.tatamiNumber}</Tag>
                    )
                  }
                >
                  <ParticipantRow $isWinner={isWinner1} $isLoser={!!(isCompleted && !isWinner1 && match.participant2Id)}>
                    <ParticipantName>
                      {participant1 ? (
                        <>
                          <UserOutlined />
                          <span>{participant1.lastName} {participant1.firstName}</span>
                        </>
                      ) : (
                        <span style={{ color: '#bfbfbf', fontStyle: 'italic' }}>TBD</span>
                      )}
                    </ParticipantName>
                    {isCompleted && match.participant1Score !== undefined && (
                      <ScoreDisplay>{match.participant1Score}</ScoreDisplay>
                    )}
                    {isWinner1 && <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />}
                  </ParticipantRow>

                  <Divider style={{ margin: '12px 0' }}>VS</Divider>

                  <ParticipantRow $isWinner={isWinner2} $isLoser={!!(isCompleted && !isWinner2 && match.participant1Id)}>
                    <ParticipantName>
                      {participant2 ? (
                        <>
                          <UserOutlined />
                          <span>{participant2.lastName} {participant2.firstName}</span>
                        </>
                      ) : (
                        <span style={{ color: '#bfbfbf', fontStyle: 'italic' }}>TBD</span>
                      )}
                    </ParticipantName>
                    {isCompleted && match.participant2Score !== undefined && (
                      <ScoreDisplay>{match.participant2Score}</ScoreDisplay>
                    )}
                    {isWinner2 && <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />}
                  </ParticipantRow>
                </MatchCard>
              );
            })}
          </MatchesGrid>
        </Col>

        <Col xs={24} lg={8}>
          <Title level={4} style={{ marginBottom: 16 }}>
            Турнирная таблица
          </Title>
          <StandingsTable
            dataSource={standings}
            rowKey={(record: ParticipantStats) => record.participant.id || (record.participant as any).userId || Math.random()}
            pagination={false}
            size="small"
            columns={[
              {
                title: 'Место',
                key: 'place',
                width: 80,
                render: (_: any, record: ParticipantStats) => (
                  <MedalBadge $place={record.place}>
                    {record.place === 1 ? '🥇' : record.place === 2 ? '🥈' : record.place === 3 ? '🥉' : record.place}
                  </MedalBadge>
                ),
              },
              {
                title: 'Участник',
                key: 'name',
                render: (_: any, record: ParticipantStats) => (
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {record.participant.lastName} {record.participant.firstName}
                    </div>
                    {record.participant.club?.name && (
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                        {record.participant.club.name}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                title: 'Очки',
                dataIndex: 'points',
                key: 'points',
                width: 80,
                align: 'center' as const,
                render: (points: number) => (
                  <Tag color="blue" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                    {points}
                  </Tag>
                ),
                sorter: (a: ParticipantStats, b: ParticipantStats) => b.points - a.points,
              },
              {
                title: 'П',
                dataIndex: 'wins',
                key: 'wins',
                width: 60,
                align: 'center' as const,
                render: (wins: number) => <Tag color="green">{wins}</Tag>,
              },
              {
                title: 'П',
                dataIndex: 'losses',
                key: 'losses',
                width: 60,
                align: 'center' as const,
                render: (losses: number) => <Tag color="red">{losses}</Tag>,
              },
            ]}
          />
        </Col>
      </Row>
    </BracketContainer>
  );
};

export default RoundRobinBracket;


